import { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "../../context/AppContext";
import {
  TOUR_CHAPTERS,
  getStepsForRole,
  getChaptersForRole,
} from "../../data/helpTourContent";
import MockPageFrame, { TourHotspot, TourCursor } from "./MockPageFrame";
import {
  ChevronLeft,
  ChevronRight,
  Play,
  RotateCcw,
  ExternalLink,
  Sparkles,
  LayoutDashboard,
  Layers,
  Route,
  ListChecks,
  HelpCircle,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  MousePointer2,
} from "lucide-react";

const CHAPTER_ICONS = {
  welcome: Sparkles,
  dashboard: LayoutDashboard,
  modules: Layers,
  journey: Route,
  tasks: ListChecks,
  faq: HelpCircle,
};

const GLASS = {
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.9)",
  boxShadow: "0 8px 32px rgba(37,99,235,0.1)",
};

export default function InteractiveProductTour() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const role = currentUser?.role || "user";
  const allowedModules = currentUser?.modules || [];

  const chapters = useMemo(
    () => getChaptersForRole(role, allowedModules),
    [role, allowedModules]
  );
  const allSteps = useMemo(
    () => getStepsForRole(role, null, allowedModules),
    [role, allowedModules]
  );

  const [started, setStarted] = useState(false);
  const [chapterId, setChapterId] = useState(chapters[0]?.id || "welcome");
  const [stepIndex, setStepIndex] = useState(0);
  const [clickSeqIndex, setClickSeqIndex] = useState(0);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);

  const chapterSteps = useMemo(
    () => allSteps.filter((s) => s.chapterId === chapterId),
    [allSteps, chapterId]
  );

  useEffect(() => {
    if (!chapters.length) return;
    const chapterStillVisible = chapters.some((c) => c.id === chapterId);
    const chapterHasSteps = chapterSteps.length > 0;
    if (!chapterStillVisible || !chapterHasSteps) {
      setChapterId(chapters[0].id);
      setStepIndex(0);
      setClickSeqIndex(0);
    }
  }, [chapters, chapterId, chapterSteps.length]);

  const step = chapterSteps[stepIndex] || chapterSteps[0];
  const globalIndex = allSteps.findIndex((s) => s?.id === step?.id);
  const totalSteps = allSteps.length;

  const activeClick = step?.clickSequence?.[clickSeqIndex];
  const activeHotspotId = activeClick?.hotspotId || null;

  const activeHotspot = step?.hotspots?.find((h) => h.id === activeHotspotId);
  const cursorPos = activeHotspot
    ? { x: activeHotspot.x + activeHotspot.w / 2, y: activeHotspot.y + activeHotspot.h / 2 }
    : null;

  const goToStep = useCallback(
    (chId, idx) => {
      setChapterId(chId);
      setStepIndex(idx);
      setClickSeqIndex(0);
      setHoveredHotspot(null);
    },
    []
  );

  const handleNext = () => {
    if (clickSeqIndex < (step?.clickSequence?.length || 0) - 1) {
      setClickSeqIndex((i) => i + 1);
      return;
    }
    if (stepIndex < chapterSteps.length - 1) {
      setStepIndex((i) => i + 1);
      setClickSeqIndex(0);
      return;
    }
    const chIdx = chapters.findIndex((c) => c.id === chapterId);
    if (chIdx < chapters.length - 1) {
      const next = chapters[chIdx + 1];
      goToStep(next.id, 0);
      return;
    }
    setStarted(false);
  };

  const handlePrev = () => {
    if (clickSeqIndex > 0) {
      setClickSeqIndex((i) => i - 1);
      return;
    }
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setClickSeqIndex(0);
      return;
    }
    const chIdx = chapters.findIndex((c) => c.id === chapterId);
    if (chIdx > 0) {
      const prev = chapters[chIdx - 1];
      const prevSteps = allSteps.filter((s) => s.chapterId === prev.id);
      goToStep(prev.id, prevSteps.length - 1);
    }
  };

  const handleHotspotClick = (hotspotId) => {
    const seqIdx = step?.clickSequence?.findIndex((c) => c.hotspotId === hotspotId);
    if (seqIdx >= 0) setClickSeqIndex(seqIdx);
  };

  const progressPct = totalSteps > 0 ? ((globalIndex + 1) / totalSteps) * 100 : 0;

  if (!started) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0a1628 0%, #0f2d6b 50%, #112366 100%)",
          boxShadow: "0 12px 40px rgba(15,23,42,0.25)",
        }}
      >
        <div className="p-8 sm:p-12 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 30% 20%, rgba(99,102,241,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(14,165,233,0.3) 0%, transparent 50%)",
            }}
          />
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-400 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <MousePointer2 size={28} className="text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3">
              Interactive Product Tour
            </h2>
            <p className="text-sky-100/90 text-sm sm:text-base max-w-xl mx-auto mb-2 leading-relaxed">
              Follow highlighted buttons, animated cursor pointers, and step-by-step
              instructions to learn every module — no prior training needed.
              Only modules enabled for your organisation are included.
            </p>
            <p className="text-sky-300/70 text-xs mb-8">
              {totalSteps} steps tailored for your role · {chapters.length} chapters
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {chapters.map((ch) => {
                const Icon = CHAPTER_ICONS[ch.id] || Sparkles;
                return (
                  <span
                    key={ch.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-sky-100 text-xs"
                  >
                    <Icon size={12} />
                    {ch.title}
                  </span>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                setStarted(true);
                goToStep(chapters[0]?.id || "welcome", 0);
              }}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-white text-indigo-700 font-semibold text-sm hover:bg-sky-50 transition-colors shadow-xl"
            >
              <Play size={18} fill="currentColor" />
              Start Interactive Tour
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!step) return null;

  const chapter = TOUR_CHAPTERS.find((c) => c.id === chapterId);

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="rounded-xl overflow-hidden bg-slate-200/60 h-1.5">
        <motion.div
          className="h-full bg-gradient-to-r from-indigo-500 to-sky-400"
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      <div className="flex flex-col xl:flex-row gap-4">
        {/* Chapter sidebar */}
        <aside className="xl:w-52 shrink-0">
          <div className="rounded-2xl p-3 sticky top-4" style={GLASS}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-2 mb-2">
              Chapters
            </p>
            <nav className="space-y-0.5">
              {chapters.map((ch) => {
                const Icon = CHAPTER_ICONS[ch.id] || Sparkles;
                const count = allSteps.filter((s) => s.chapterId === ch.id).length;
                const isActive = ch.id === chapterId;
                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => goToStep(ch.id, 0)}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-xs transition-colors ${
                      isActive
                        ? "bg-indigo-50 text-indigo-700 font-semibold"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon size={14} className={isActive ? "text-indigo-500" : "text-slate-400"} />
                    <span className="flex-1 truncate">{ch.title}</span>
                    <span className="text-[10px] text-slate-400">{count}</span>
                  </button>
                );
              })}
            </nav>
            <button
              type="button"
              onClick={() => setStarted(false)}
              className="w-full mt-3 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs text-slate-500 hover:bg-slate-50 border border-slate-200"
            >
              <RotateCcw size={12} />
              Restart Tour
            </button>
          </div>
        </aside>

        {/* Main tour panel */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Step header */}
          <div className="rounded-2xl p-4 sm:p-5" style={GLASS}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div>
                <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide">
                  {chapter?.title} · Step {stepIndex + 1}/{chapterSteps.length}
                </span>
                <h3 className="text-lg font-display font-bold text-slate-800 mt-0.5">
                  {step.title}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className="font-medium text-slate-600">{step.pageName}</span>
                  {" — "}
                  {step.purpose}
                </p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 font-medium">
                {globalIndex + 1} / {totalSteps}
              </span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
          </div>

          {/* Screenshot + annotations */}
          <div className="rounded-2xl p-4 sm:p-5" style={GLASS}>
            <div className="flex items-center gap-2 mb-3">
              <MousePointer2 size={14} className="text-indigo-500" />
              <span className="text-xs font-semibold text-slate-700">
                Interactive Preview — hover hotspots or follow the numbered sequence
              </span>
            </div>

            <div className="relative max-w-3xl mx-auto">
              <MockPageFrame
                frame={step.frame}
                frameLabel={step.frameLabel}
                frameColor={step.frameColor}
              />

              {/* Hotspot overlay layer */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="relative w-full h-full pointer-events-auto">
                  {step.hotspots?.map((hs, i) => (
                    <TourHotspot
                      key={hs.id}
                      hotspot={hs}
                      index={i}
                      isActive={hs.id === activeHotspotId}
                      isHovered={hs.id === hoveredHotspot}
                      onHover={setHoveredHotspot}
                      onClick={handleHotspotClick}
                    />
                  ))}
                  {cursorPos && (
                    <TourCursor x={cursorPos.x} y={cursorPos.y} visible />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Click sequence panel */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl p-4 sm:p-5" style={GLASS}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <ListChecks size={14} className="text-indigo-500" />
                Click Sequence
              </p>
              <div className="space-y-2">
                {step.clickSequence?.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setClickSeqIndex(i)}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      i === clickSeqIndex
                        ? "border-indigo-300 bg-indigo-50/80 ring-2 ring-indigo-200/50"
                        : "border-slate-200 bg-white/50 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                          i === clickSeqIndex
                            ? "bg-indigo-600 text-white"
                            : i < clickSeqIndex
                              ? "bg-emerald-500 text-white"
                              : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {i < clickSeqIndex ? "✓" : item.step}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800">{item.action}</p>
                        <p className="text-[11px] text-emerald-700 mt-1 flex items-start gap-1">
                          <CheckCircle2 size={10} className="shrink-0 mt-0.5" />
                          {item.outcome}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {/* Active step detail */}
              <AnimatePresence mode="wait">
                {activeClick && (
                  <motion.div
                    key={clickSeqIndex}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    className="rounded-2xl p-4 border-2 border-indigo-200 bg-indigo-50/50"
                  >
                    <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1">
                      Current Action (Step {activeClick.step})
                    </p>
                    <p className="text-sm font-medium text-slate-800">{activeClick.action}</p>
                    <p className="text-xs text-emerald-700 mt-2">
                      Expected: {activeClick.outcome}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tips & warnings */}
              {(step.tips?.length > 0 || step.warnings?.length > 0) && (
                <div className="rounded-2xl p-4 space-y-2" style={GLASS}>
                  {step.tips?.map((t) => (
                    <div key={t} className="flex gap-2 text-xs text-slate-600">
                      <Lightbulb size={14} className="text-amber-500 shrink-0" />
                      {t}
                    </div>
                  ))}
                  {step.warnings?.map((w) => (
                    <div key={w} className="flex gap-2 text-xs text-red-700 bg-red-50/80 p-2 rounded-lg">
                      <AlertTriangle size={14} className="shrink-0" />
                      {w}
                    </div>
                  ))}
                </div>
              )}

              {/* Expected outcome */}
              <div className="rounded-2xl p-4 bg-emerald-50/60 border border-emerald-100">
                <p className="text-[10px] font-semibold text-emerald-700 uppercase mb-1">
                  Expected Outcome
                </p>
                <p className="text-sm text-slate-700">{step.expectedOutcome}</p>
              </div>
            </div>
          </div>

          {/* Navigation footer */}
          <div
            className="rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={GLASS}
          >
            <p className="text-xs text-slate-500 italic">{step.nextHint}</p>
            <div className="flex items-center gap-2">
              {step.livePath && (
                <button
                  type="button"
                  onClick={() => navigate(step.livePath)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors"
                >
                  Try Live
                  <ExternalLink size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={handlePrev}
                disabled={globalIndex === 0 && clickSeqIndex === 0}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-1 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200"
              >
                {globalIndex === totalSteps - 1 &&
                clickSeqIndex === (step.clickSequence?.length || 1) - 1
                  ? "Finish Tour"
                  : clickSeqIndex < (step.clickSequence?.length || 0) - 1
                    ? "Next Click"
                    : "Next Step"}
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
